import queryClient from '@/api/queryClient';
import { orpc } from '@/api/rpc/client';
import { Action, Subject, AbilityRulesDTO } from '@erp/authz';
import { AuthzCanOutput } from '@erp/rpc';
import { QueryClient } from '@tanstack/react-query';

// Cache validity: keep permissions for 30 minutes since we now have reliable
// updates via useCurrentUser and dynamic fetches replace stale initial entries
export const AUTHZ_CACHE_TIME_MS = 30 * 60 * 1000; // 30 minutes

// Internal marker for cache entries populated by initializeFromAbilityRules.
// Not exported. Used to selectively clear and update base abilities.
const INITIAL_MARKER = Symbol('authz-initial-marker');

export function getAuthzQueryKey(
  {
    action,
    subject,
  }: {
    action: Action;
    subject: Subject;
  },
  isInitial = false,
) {
  // The marker sits at index 1 so initial entries form a distinct key prefix
  // (['authz', INITIAL_MARKER, ...]) that setQueryDefaults can target.
  return [
    'authz',
    ...(isInitial ? [INITIAL_MARKER] : []),
    ...orpc.authz.can.queryKey({
      input: { action, subject },
    }),
  ];
}

class AuthzClient {
  private queryClient: QueryClient;
  /**
   * The rules array the initial cache was last seeded from. `/auth/me` is one
   * shared query, so every `useAuth` mount sees the same array until the user
   * is refetched; comparing by identity is enough to skip redundant seeding.
   */
  private seededRules: AbilityRulesDTO | null = null;

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
    // Entries seeded via setQueryData (initializeFromAbilityRules) have no
    // observers, so without this they are garbage collected after the
    // client's default gcTime (5 min) and route filtering silently loses all
    // permissions. Pin only the initial entries; the ones useCan fetches carry
    // their own gcTime from the hook. reset() still clears on logout.
    this.queryClient.setQueryDefaults(['authz', INITIAL_MARKER], {
      gcTime: Infinity,
    });
  }

  /**
   * Subscribe to authz cache changes (seeding on login, re-seeding when
   * ability rules refresh, clearing on logout, dynamic fetches).
   * Returns an unsubscribe function.
   */
  subscribe(listener: () => void): () => void {
    return this.queryClient.getQueryCache().subscribe((event) => {
      // Not 'added': useCan builds its query while a component renders, and a
      // query is added empty, so its permission is unchanged until the
      // 'updated' that carries data. Notifying there made subscribers set
      // state during another component's render.
      if (
        (event.type === 'removed' || event.type === 'updated') &&
        event.query.queryKey[0] === 'authz'
      ) {
        listener();
      }
    });
  }

  /**
   * Reset the authorization cache.
   * Should be called when a user logs out or when switching users
   * to prevent permission leakage between user sessions.
   */
  reset(): void {
    this.seededRules = null;
    this.queryClient.removeQueries({ queryKey: ['authz'] });
  }

  /**
   * Imperative API to check if an action is allowed on a subject
   *
   * @param action - The action to perform
   * @param subject - The subject/resource type
   * @returns Promise resolving to true if allowed, false otherwise
   *
   * @example
   * const canApprove = await authzClient.can('approve', 'APInvoice');
   */
  async can(action: Action, subject: Subject): Promise<boolean> {
    // Check non-initial cache first (most recent dynamic fetches)
    const cachedData = this.queryClient.getQueryData<AuthzCanOutput>(
      getAuthzQueryKey({ action, subject }, false),
    );

    if (cachedData) {
      return cachedData.allowed;
    }

    // Fall back to initial cache (from initializeFromAbilityRules)
    const initialData = this.queryClient.getQueryData<AuthzCanOutput>(
      getAuthzQueryKey({ action, subject }, true),
    );

    if (initialData) {
      return initialData.allowed;
    }

    const { allowed } = await orpc.authz.can.call({ action, subject });

    // Every check is a base ability, so refresh the initial entry the sidebar
    // and route filtering read from.
    this.addPermissionToCache({ action, subject }, allowed, true);

    return allowed;
  }

  /**
   * Clear all initial ability cache entries (marked with INITIAL_MARKER symbol)
   *
   * Used to remove stale base abilities before repopulating via initializeFromAbilityRules.
   * Does not affect dynamically-fetched permissions (those without INITIAL_MARKER).
   */
  clearInitialAbilities(): void {
    this.seededRules = null;
    this.queryClient.removeQueries({
      queryKey: ['authz'],
      predicate: (query) => {
        const key = query.queryKey;
        // Only remove queries that have INITIAL_MARKER in the key
        return Array.isArray(key) && key.includes(INITIAL_MARKER);
      },
    });
  }

  /**
   * Replace the seeded base abilities with `rules`, unless `rules` is the
   * very array already seeded. `useAuth` runs this from an effect on every
   * mount, and a detail panel can mount several `useAuth` callers at once;
   * without this check each of them cleared and re-wrote every ability,
   * and every write made each `useRoutePermissions` subscriber rebuild its
   * whole map — hundreds of milliseconds of main-thread work per mount.
   */
  seedAbilityRules(rules: AbilityRulesDTO): void {
    if (rules === this.seededRules) return;
    this.clearInitialAbilities();
    this.initializeFromAbilityRules(rules);
  }

  /**
   * Initialize the cache with base ability rules from the backend
   *
   * Only rules without conditions are cached (base abilities).
   * Rules with conditions require instance-specific checks and cannot be pre-cached.
   *
   * Can be called multiple times (e.g., when useCurrentUser updates) to refresh the cache.
   * Each call marks entries with 'initial' tag for selective clearing.
   *
   * @param rules - Ability rules from getCurrentUser response
   */
  initializeFromAbilityRules(rules: AbilityRulesDTO): void {
    this.seededRules = rules;
    // Filter to only unconditional rules (base abilities)
    // Rules with conditions cannot be pre-cached
    const baseAbilities = rules.filter((rule) => !rule.conditions);

    // Convert to cache entries with 'initial' marker
    const cacheEntries: [
      { action: Action; subject: Subject },
      boolean,
      boolean, // isInitial flag
    ][] = baseAbilities.map((rule) => [
      { action: rule.action, subject: rule.subject },
      !rule.inverted, // inverted rules are denials
      true, // isInitial
    ]);

    // Bulk insert into cache
    cacheEntries.forEach(([permissionWithRecord, allowed, isInitial]) => {
      this.addPermissionToCache(permissionWithRecord, allowed, isInitial);
    });
  }

  addPermissionToCache(
    { action, subject }: { action: Action; subject: Subject },
    allowed: boolean,
    isInitial = false,
  ) {
    const queryKey = getAuthzQueryKey({ action, subject }, isInitial);

    // Set the data in cache
    // The cache entry will be considered fresh until AUTHZ_CACHE_TIME_MS passes
    // This is enforced by useCan's staleTime configuration
    this.queryClient.setQueryData<AuthzCanOutput>(
      queryKey,
      {
        allowed,
      },
      {
        // Set the updated timestamp to now, so staleness calculation starts from this moment
        updatedAt: Date.now(),
      },
    );
  }

  addPermissionsToCache(
    permissions: [{ action: Action; subject: Subject }, boolean][],
  ) {
    permissions.forEach(([permission, allowed]) => {
      this.addPermissionToCache(permission, allowed);
    });
  }

  /**
   * Synchronously check if a permission is cached.
   * Returns the cached value or undefined if not in cache.
   *
   * This is useful for synchronous permission checks (e.g., route filtering)
   * when the cache has been pre-populated via initializeFromAbilityRules.
   *
   * Checks the initial cache entries first (INITIAL_MARKER), then falls back
   * to dynamically-fetched entries. Dynamic fetches for base abilities overwrite
   * initial entries, so this effectively prefers fresh data.
   *
   * @param action - The action to check
   * @param subject - The subject/resource type
   * @returns boolean if cached, undefined if not in cache
   */
  getCachedPermission(action: Action, subject: Subject): boolean | undefined {
    // Try initial abilities first (refreshed by dynamic fetches)
    const initialData = this.queryClient.getQueryData<AuthzCanOutput>(
      getAuthzQueryKey({ action, subject }, true),
    );
    if (initialData) {
      return initialData.allowed;
    }

    // Fall back to dynamically-fetched permissions
    const cachedData = this.queryClient.getQueryData<AuthzCanOutput>(
      getAuthzQueryKey({ action, subject }, false),
    );
    return cachedData?.allowed;
  }
}

export const authzClient = new AuthzClient(queryClient);
export type { AuthzClient };
